import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useCurrency from 'hooks/useCurreny';
import useGetMetric from 'hooks/useGetMetric';
import Button from 'components/Button';
import {
  numberFormatter, numberReFormatter, unMaskInput,
} from 'utils/helpers';

export interface Props {
  alkali: string;
  concentration: string;
  consumption: string;
  price: string;
  annualExpenditure: string | number;
  omya: string;
  concentrationOmya: string;
  costDelivered: string;
  expectedConsumption: number;
  setResult: (value: boolean) => void;
}

const ProductConverterResult: FC<Props> = (props) => {
  const {
    consumption,
    price,
    alkali,
    concentration,
    setResult,
    omya,
    costDelivered,
    annualExpenditure,
    expectedConsumption,
  } = props;
  const { t } = useTranslation();
  const { ton } = useGetMetric();
  const { currencyBlue } = useCurrency();

  const currentProductArr = [
    {
      id: 1,
      title: `${t('currentAlkali')}:`,
      value: alkali,
    },
    {
      id: 2,
      title: `${t('concentration')}:`,
      value:
        concentration === '100'
          ? concentration + ' = Powder'
          : concentration + ' wt.%',
    },
    {
      id: 3,
      title: `${t('annualConsumption')}:`,
      value: consumption + ` ${ton}`,
    },
    {
      id: 4,
      title: `${t('price')}:`,
      value: price + ` ${currencyBlue}/` + ` ${ton}`,
    },
    {
      id: 5,
      title: `${t('currentExpenditure')}:`,
      value: annualExpenditure + ` ${currencyBlue}/` + t('year'),
    },
  ];

  const annualSavings = useMemo(() => {
    if (!costDelivered || !expectedConsumption) {
      return 0;
    }

    const omyaSum = Number(unMaskInput(costDelivered)) * expectedConsumption;
    const reformattedAnnualExpenditure = numberReFormatter(annualExpenditure);

    return Number(reformattedAnnualExpenditure) - omyaSum;
  }, [costDelivered, expectedConsumption, annualExpenditure]);

  const relativeSavings = useMemo(() => {
    if (!annualSavings || !annualExpenditure) {
      return 0;
    }

    const reformattedAnnualExpenditure = numberReFormatter(annualExpenditure);

    const percent =
      (annualSavings * 100) / Number(reformattedAnnualExpenditure);

    return percent;
  }, [annualSavings, annualExpenditure]);

  const omyaProductArr = useMemo(
    () => [
      {
        id: 1,
        title: `${t('omyaProduct')}:`,
        value: omya,
        color: 'black',
      },
      {
        id: 2,
        title: `${t('expectedConsumption')}:`,
        value: expectedConsumption + ` ${ton}/` + t('year'),
        color: 'black',
      },
      {
        id: 3,
        title: `${t('productCost')}:`,
        value:
          numberFormatter(
            Number(unMaskInput(costDelivered)) * expectedConsumption,
          ) +
          ` ${currencyBlue}/` +
          t('year'),
        color: 'black',
      },
      {
        id: 4,
        title: `${t('savings')}:`,
        value: numberFormatter(annualSavings) + ` ${currencyBlue}/` + t('year'),
        color: annualSavings > 0 ? 'green' : 'orange',
      },
      {
        id: 5,
        title: `${t('relativeSavings')}:`,
        value: Math.round(relativeSavings) + ' %',
        color: 'black',
      },
    ],
    [
      relativeSavings,
      annualSavings,
      costDelivered,
      expectedConsumption,
      omya,
      currencyBlue,
    ],
  );

  return (
    <>
      <div className="flex justify-between items-center md:flex-col">
        <div className="border border-primitiveGrey p-8 md:py-6 md:px-4 rounded-2xl w-full mr-3 md:mr-0">
          <div className="text-xl	text-black font-bold mb-6 md:text-lg">
            {t('currentProductTitle')}
          </div>
          <div>
            {currentProductArr.map((currentProduct) => (
              <div className="flex" key={currentProduct.id}>
                <div className="text-secondaryText mb-2 whitespace-nowrap">
                  {currentProduct.title} &nbsp;
                </div>
                <div className="font-medium">{currentProduct.value}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="w-full ml-3 md:ml-0 md:mt-4 border border-primary bg-primitiveBlue p-8 md:py-6 md:px-4 rounded-2xl">
          <div className="text-xl	text-black font-bold mb-6 md:text-lg">
            {t('omyaOffered')}
          </div>
          <div>
            {omyaProductArr.map((omyaProduct) => (
              <div className="flex" key={omyaProduct.id}>
                <div className="text-secondaryText mb-2">
                  {omyaProduct.title} &nbsp;
                </div>
                {omyaProduct.id === 4 ? (
                  <>
                    {omyaProduct.color === 'orange' ? (
                      <div className={'font-medium text-orange'}>
                        {omyaProduct.value}
                      </div>
                    ) : (
                      <div className={'font-medium text-green'}>
                        {omyaProduct.value}
                      </div>
                    )}
                  </>
                ) : (
                  <div className={'font-medium text-black'}>
                    {omyaProduct.value}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="md:mt-4 mt-6 w-full flex justify-center">
        <Button
          onClick={() => setResult(false)}
          className="w-[166px]"
          btnType="secondary"
        >
          {t('back')}
        </Button>
      </div>
    </>
  );
};

export default ProductConverterResult;
